import { AIRecommendation } from '@/apps/AIAssistant/types';
import { supabase } from '@/common/config/supabase';
import handleException from '@/common/utils/errors';
import i18n from '@/i18n';

interface GoogleCalendarEvent {
  id: string;
  title: string;
  time: string;
  meetLink?: string;
}

/**
 * Generates AI recommendations using the Supabase Edge Function
 * The function automatically fetches:
 * - Calendar events from the database (calendar_events table)
 * - Checklist items from the database (checklist_items table)
 * - Schedule events from widget metadata
 *
 * You only need to pass Google Calendar events (external data)
 *
 * @param googleCalendarEvents - Optional array of Google Calendar events from useScheduleStore
 * @returns Promise<AIRecommendation[]>
 */
export const generateAIRecommendationsViaSupabase = async (
  googleCalendarEvents?: GoogleCalendarEvent[],
): Promise<AIRecommendation[]> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('User not authenticated');
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const response = await fetch(`${supabaseUrl}/functions/v1/ai-recommendations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        googleCalendarEvents: googleCalendarEvents || [],
        language: i18n.language,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const recommendations: AIRecommendation[] = await response.json();
    return recommendations;
  } catch (error) {
    handleException(error);
    throw error;
  }
};
